import { useQuery, useReactiveVar } from "@apollo/client";
import { useMemo, useState } from "react";
import { useSearch } from "react-location";
import { GET_SERVICE_ORDER } from "./apollo";
import { LocationGenerics } from "router/location";
import { Modal, ServiceOrderHistoryView } from "components";
import { classNames, dispatchModal, wrapClick } from "utils";
import { currentUserVar } from "apollo/cache/auth";
import {
  InspectionServiceOrderDetails,
  InspectionServiceOrderResolutionDetails,
} from "components/cards/inspection-service-order-view";

const orderTabs = [
  {
    name: "Order Details",
    href: "OrderDetails",
    activeStatuses: [
      "Pending",
      "Assigned",
      "Resolved",
      "Completed",
      "InProgress",
    ],
  },
  {
    name: "Order History",
    href: "OrderHistory",
    activeStatuses: [
      "Pending",
      "Assigned",
      "Resolved",
      "Completed",
      "InProgress",
      "Disapproved",
    ],
  },
  {
    name: "Resolution Details",
    href: "ResolutionDetails",
    activeStatuses: ["Resolved", "Disapproved", "Completed"],
  },
];

export default function ViewInspectionServiceOrderContainer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const searchParams = useSearch<LocationGenerics>();
  const [orderTab, setOrderTab] = useState("OrderDetails");
  const currentUser = useReactiveVar(currentUserVar);
  const { data, loading } = useQuery(GET_SERVICE_ORDER, {
    variables: {
      id: searchParams.id,
    },
    notifyOnNetworkStatusChange: false,
    fetchPolicy: "network-only",
  });

  const serviceOrder = data?.serviceOrder;

  const canPerform = useMemo(
    () => serviceOrder?.assigner?._id === currentUser?._id,
    [serviceOrder?.assigner?._id, currentUser?._id],
  );

  const __setOrderTab = (tab: string) => () => {
    setOrderTab(tab);
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      hideActions={false}
      hidePadding={true}
      loading={loading}
      title="Service Order Information"
      size="4xl"
      description="Details of service order are shown below"
      renderActions={() => (
        <>
          {["Pending", "Assigned", "InProgress"].includes(
            serviceOrder?.status,
          ) && (
            <button
              type="button"
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
              onClick={wrapClick(
                dispatchModal(serviceOrder?._id, "assign"),
              )}
            >
              {serviceOrder?.status === "Pending" ? "Assign" : "Reassign"}
            </button>
          )}
          {serviceOrder?.status === "Resolved" && canPerform && (
            <>
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(
                  dispatchModal(serviceOrder?._id, "approve"),
                )}
              >
                Approve
              </button>
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={wrapClick(
                  dispatchModal(serviceOrder?._id, "disapprove"),
                )}
              >
                Disapprove
              </button>
            </>
          )}
        </>
      )}
    >
      {serviceOrder?._id && (
        <>
          {[
            "Assigned",
            "InProgress",
            "Resolved",
            "Completed",
            "Disapproved",
          ].includes(serviceOrder.status) && (
            <div className="block">
              <div className="border-b border-gray-200 bg-white px-6">
                <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                  {orderTabs.map((_orderTab) => (
                    <button
                      key={_orderTab.name}
                      onClick={wrapClick(__setOrderTab(_orderTab.href))}
                      disabled={
                        !_orderTab.activeStatuses.includes(serviceOrder.status)
                      }
                      className={classNames(
                        orderTab === _orderTab.href
                          ? "border-primary-500 text-primary-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-200",
                        "whitespace-nowrap disabled:cursor-not-allowed flex py-4 px-1 border-b-2 font-medium text-sm",
                      )}
                      aria-current={
                        orderTab === _orderTab.href ? "page" : undefined
                      }
                    >
                      {_orderTab.name}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}
          <div className="flex-1 w-full min-h-[65vh] max-h-[65vh] overflow-y-auto  sm:p-6">
            {orderTab === "OrderDetails" && (
              <InspectionServiceOrderDetails serviceOrder={serviceOrder} />
            )}
            {orderTab === "OrderHistory" && (
              <ServiceOrderHistoryView history={serviceOrder.history} />
            )}
            {orderTab === "ResolutionDetails" && (
              <InspectionServiceOrderResolutionDetails
                region={serviceOrder.region}
                district={serviceOrder.district}
                resolution={serviceOrder.resolution}
                result={serviceOrder.result || ""}
              />
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
